/**
 * MapPlacementStep Component
 *
 * Allows setters to tap on the floor plan map to place a route pin.
 * Auto-detects wall section based on polygon containment.
 *
 * Features:
 * - Floor plan image as background
 * - Wall section polygons with labels
 * - Tap to place route pin
 * - Auto wall section detection
 * - Preview pin at tap location
 *
 * @example
 * ```tsx
 * <MapPlacementStep
 *   floorPlan={gym.floorPlan}
 *   colorGrades={gym.colorGrades}
 *   selectedColor="Yellow"
 *   grade="V4"
 *   onPositionSelect={(position, wallSection) => {
 *     setMapPosition(position);
 *     setWallSection(wallSection);
 *   }}
 *   onCancel={() => setStep('form')}
 * />
 * ```
 */

import { useState, useMemo, useRef, useEffect, useCallback, type CSSProperties } from 'react';
import { Image as KonvaImage } from 'react-konva';
import type Konva from 'konva';
import { MapStage } from '../gym-map/MapStage';
import { WallPolygon } from '../gym-map/WallPolygon';
import { RoutePin } from '../gym-map/RoutePin';
import { useFloorPlanImage } from '@/hooks/useFloorPlanImage';
import { normalizePoint, denormalizePolygon, flattenPolygonPoints } from '@/utils/coordinates';
import { detectWallSection } from '@/utils/geometry';
import { perfStart, perfEnd, perfRender } from '@/utils/performanceMonitor';
import type { GymFloorPlan, ColorGrade, Point } from '@flash/shared';

/**
 * Props for MapPlacementStep component
 */
export interface MapPlacementStepProps {
  /** Floor plan configuration with image and regions */
  floorPlan: GymFloorPlan;
  /** Gym's color grades for pin color */
  colorGrades: ColorGrade[];
  /** Selected color grade name */
  selectedColor: string;
  /** Grade to display on pin (e.g., "V4") */
  grade: string;
  /** Called when user taps to place pin */
  onPositionSelect: (position: Point, wallSection: string | null) => void;
  /** Called when user cancels placement */
  onCancel: () => void;
  /** Initial position if editing */
  initialPosition?: Point | null;
}

/**
 * Calculate the scale to fit the image within the container while maintaining aspect ratio
 */
const calculateFitScale = (
  imageWidth: number,
  imageHeight: number,
  containerWidth: number,
  containerHeight: number
): number => {
  const scaleX = containerWidth / imageWidth;
  const scaleY = containerHeight / imageHeight;
  return Math.min(scaleX, scaleY);
};

/**
 * Colors for wall section polygons in placement view
 */
const WALL_POLYGON_COLORS = {
  fill: 'rgba(59, 130, 246, 0.1)',
  fillHover: 'rgba(59, 130, 246, 0.2)',
  fillSelected: 'rgba(59, 130, 246, 0.3)',
  fillHighlight: 'rgba(59, 130, 246, 0.4)',
  stroke: 'rgba(59, 130, 246, 0.5)',
  labelColor: 'rgba(59, 130, 246, 0.8)',
};

/**
 * MapPlacementStep Component
 *
 * Allows setters to place route pins on the floor plan map.
 */
export const MapPlacementStep = ({
  floorPlan,
  colorGrades,
  selectedColor,
  grade,
  onPositionSelect,
  onCancel,
  initialPosition,
}: MapPlacementStepProps) => {
  // Performance tracking
  perfRender('MapPlacementStep');

  // Container ref for measuring dimensions
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Selected pin position (normalized 0-1)
  const [pinPosition, setPinPosition] = useState<Point | null>(initialPosition ?? null);

  // Load the floor plan image (must be before useEffect that depends on isLoading)
  const { image, isLoading, hasError, naturalWidth, naturalHeight } = useFloorPlanImage(
    floorPlan.imageUrl
  );

  // Update dimensions on resize with robust measurement
  // Note: depends on isLoading because containerRef isn't mounted during loading state
  useEffect(() => {
    // Don't try to measure while still loading the image
    if (isLoading) return;

    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      // Use getBoundingClientRect for more reliable measurement
      const rect = container.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      // Only update if we have valid dimensions
      if (width > 0 && height > 0) {
        setDimensions((prev) => {
          // Avoid unnecessary re-renders
          if (prev.width === width && prev.height === height) {
            return prev;
          }
          return { width, height };
        });
      }
    };

    // Immediate attempt
    updateDimensions();

    // Use requestAnimationFrame to ensure layout is complete
    const rafId = requestAnimationFrame(() => {
      updateDimensions();
    });

    // Fallback: keep polling until we get dimensions (max 2 seconds)
    let attempts = 0;
    const maxAttempts = 20;
    const intervalId = setInterval(() => {
      attempts++;
      const rect = container.getBoundingClientRect();
      if ((rect.width > 0 && rect.height > 0) || attempts >= maxAttempts) {
        updateDimensions();
        clearInterval(intervalId);
      }
    }, 100);

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(intervalId);
      resizeObserver.disconnect();
    };
  }, [isLoading]);

  // Use stored dimensions if available, otherwise use loaded dimensions
  const imageWidth = floorPlan.imageWidth || naturalWidth;
  const imageHeight = floorPlan.imageHeight || naturalHeight;

  // Get pin color from color grades
  const pinColor = useMemo(() => {
    const colorGrade = colorGrades.find((cg) => cg.displayName === selectedColor);
    return colorGrade?.color ?? '#808080';
  }, [colorGrades, selectedColor]);

  // Calculate initial scale to fit image in container
  const initialScale = useMemo(() => {
    if (imageWidth === 0 || imageHeight === 0 || dimensions.width === 0) return 1;
    return calculateFitScale(imageWidth, imageHeight, dimensions.width, dimensions.height);
  }, [imageWidth, imageHeight, dimensions.width, dimensions.height]);

  // Calculate initial position to center the image
  const initialViewport = useMemo(() => {
    if (imageWidth === 0 || imageHeight === 0 || dimensions.width === 0) {
      return { x: 0, y: 0, scale: 1 };
    }

    const scaledWidth = imageWidth * initialScale;
    const scaledHeight = imageHeight * initialScale;

    return {
      x: (dimensions.width - scaledWidth) / 2,
      y: (dimensions.height - scaledHeight) / 2,
      scale: initialScale,
    };
  }, [imageWidth, imageHeight, dimensions.width, dimensions.height, initialScale]);

  // Convert normalized polygon coordinates to pixel coordinates
  const denormalizedRegions = useMemo(() => {
    perfStart('denormalizedRegions');
    if (imageWidth === 0 || imageHeight === 0) {
      perfEnd('denormalizedRegions');
      return [];
    }

    const result = floorPlan.regions.map((region) => ({
      ...region,
      pixelPoints: flattenPolygonPoints(
        denormalizePolygon(region.polygon, { width: imageWidth, height: imageHeight })
      ),
    }));
    perfEnd('denormalizedRegions');
    return result;
  }, [floorPlan.regions, imageWidth, imageHeight]);

  // Handle map tap/click for pin placement
  const handleMapClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      perfStart('handleMapClick');
      const stage = e.target.getStage();
      if (!stage) {
        perfEnd('handleMapClick');
        return;
      }

      // Get pointer position relative to stage
      const pointer = stage.getPointerPosition();
      if (!pointer) {
        perfEnd('handleMapClick');
        return;
      }

      // Convert to image coordinates (accounting for stage transform)
      const transform = stage.getAbsoluteTransform().copy().invert();
      const imagePoint = transform.point(pointer);

      // Normalize to 0-1 range
      const normalizedPoint = normalizePoint(
        { x: imagePoint.x, y: imagePoint.y },
        { width: imageWidth, height: imageHeight }
      );

      // Clamp to valid range
      const clampedPoint: Point = {
        x: Math.max(0, Math.min(1, normalizedPoint.x)),
        y: Math.max(0, Math.min(1, normalizedPoint.y)),
      };

      setPinPosition(clampedPoint);
      perfEnd('handleMapClick');
    },
    [imageWidth, imageHeight]
  );

  // Handle tap for touch devices
  const handleMapTap = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      perfStart('handleMapTap');
      const stage = e.target.getStage();
      if (!stage) {
        perfEnd('handleMapTap');
        return;
      }

      const pointer = stage.getPointerPosition();
      if (!pointer) {
        perfEnd('handleMapTap');
        return;
      }

      const transform = stage.getAbsoluteTransform().copy().invert();
      const imagePoint = transform.point(pointer);

      const normalizedPoint = normalizePoint(
        { x: imagePoint.x, y: imagePoint.y },
        { width: imageWidth, height: imageHeight }
      );

      const clampedPoint: Point = {
        x: Math.max(0, Math.min(1, normalizedPoint.x)),
        y: Math.max(0, Math.min(1, normalizedPoint.y)),
      };

      setPinPosition(clampedPoint);
      perfEnd('handleMapTap');
    },
    [imageWidth, imageHeight]
  );

  // Handle confirm placement
  const handleConfirm = () => {
    if (!pinPosition) return;

    // Detect wall section from position
    const wallSection = detectWallSection(pinPosition, floorPlan.regions);
    onPositionSelect(pinPosition, wallSection);
  };

  // Calculate pin pixel position for preview
  const pinPixelPosition = useMemo(() => {
    if (!pinPosition) return null;
    return {
      x: pinPosition.x * imageWidth,
      y: pinPosition.y * imageHeight,
    };
  }, [pinPosition, imageWidth, imageHeight]);

  // Detect current wall section
  const currentWallSection = useMemo(() => {
    if (!pinPosition) return null;
    return detectWallSection(pinPosition, floorPlan.regions);
  }, [pinPosition, floorPlan.regions]);

  // Loading state
  if (isLoading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle} />
          <p style={loadingTextStyle}>Loading floor plan...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>
          <p style={errorTextStyle}>Failed to load floor plan</p>
          <button onClick={onCancel} style={cancelButtonStyle}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check if pin is in a valid wall section
  const isValidPlacement = pinPosition !== null && currentWallSection !== null;
  const hasInvalidPlacement = pinPosition !== null && currentWallSection === null;

  return (
    <div style={wrapperStyle}>
      {/* Instructions */}
      <div style={instructionsStyle}>
        <p style={instructionTextStyle}>Tap on the map to place this route</p>
        <p style={currentWallSection ? wallSectionTextStyle : wallSectionPlaceholderStyle}>
          {currentWallSection
            ? `Wall section: ${currentWallSection}`
            : hasInvalidPlacement
              ? 'Please place inside a wall section'
              : '\u00A0'}
        </p>
      </div>

      {/* Map container */}
      <div ref={containerRef} style={mapContainerStyle}>
        {/* Show loading while measuring container dimensions */}
        {(dimensions.width === 0 || dimensions.height === 0) && (
          <div style={dimensionLoadingStyle}>
            <div style={spinnerStyle} />
            <p style={loadingTextStyle}>Preparing map...</p>
          </div>
        )}
        {dimensions.width > 0 && dimensions.height > 0 && (
          <MapStage
            width={dimensions.width}
            height={dimensions.height}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            initialViewport={initialViewport}
            minScale={initialScale * 0.5}
            maxScale={4}
            onClick={handleMapClick}
            onTap={handleMapTap}
          >
            {/* Floor plan background image */}
            {image && (
              <KonvaImage image={image} width={imageWidth} height={imageHeight} listening={false} />
            )}

            {/* Wall section polygons */}
            {denormalizedRegions.map((region) => (
              <WallPolygon
                key={region.wallSectionName}
                id={`wall-${region.wallSectionName}`}
                points={region.pixelPoints}
                label={region.wallSectionName}
                showLabel={true}
                colors={WALL_POLYGON_COLORS}
                listening={false}
              />
            ))}

            {/* Preview pin */}
            {pinPixelPosition && (
              <RoutePin
                x={pinPixelPosition.x}
                y={pinPixelPosition.y}
                grade={grade}
                holdColor={pinColor}
                size={32}
                isSelected={true}
              />
            )}
          </MapStage>
        )}
      </div>

      {/* Action buttons */}
      <div style={actionsStyle}>
        <button onClick={onCancel} style={cancelButtonStyle}>
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={!isValidPlacement}
          style={{
            ...confirmButtonStyle,
            ...(isValidPlacement ? {} : disabledButtonStyle),
          }}
        >
          Confirm Location
        </button>
      </div>
    </div>
  );
};

// Styles
const wrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100%',
};

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
};

const instructionsStyle: CSSProperties = {
  padding: '16px 20px',
  backgroundColor: 'var(--color-bg-primary, white)',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};

const instructionTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary, #1f2937)',
};

const wallSectionTextStyle: CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const wallSectionPlaceholderStyle: CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.75rem',
  color: 'var(--color-error, #ef4444)',
};

const mapContainerStyle: CSSProperties = {
  flex: '1 1 0%',
  minHeight: '300px',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  overflow: 'hidden',
  position: 'relative',
};

const dimensionLoadingStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  padding: '16px 20px',
  backgroundColor: 'var(--color-bg-primary, white)',
  borderTop: '1px solid var(--color-border, #e5e7eb)',
};

const cancelButtonStyle: CSSProperties = {
  flex: 1,
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  color: '#374151',
  backgroundColor: 'white',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
};

const confirmButtonStyle: CSSProperties = {
  flex: 2,
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: '#9ca3af',
  cursor: 'not-allowed',
};

const loadingStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
};

const spinnerStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  border: '3px solid var(--color-border, #e5e7eb)',
  borderTopColor: 'var(--color-primary, #3b82f6)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const errorStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-error, #ef4444)',
};

export default MapPlacementStep;
